import { useEffect, useMemo, useState } from "react";
import { useIsMounted } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import {
  AnyArticleSortInput,
  RentalAccommodationArticleFilterInput,
  SaleAccommodationArticleFilterInput
} from "@shared/graphql";
import { AnyArticleSearch, CustomRentalAccommodationFilterInput, useSearchContext } from "../contexts/search";
import { PaginationPayload } from "../models/search";
import * as criteriaTools from "../tools/search-params/search-criteria";
import * as paginationTools from "../tools/search-params/search-pagination";

export const formatWhere = (search: AnyArticleSearch) => {
  const { kind, ...criteria } = search;
  switch (kind) {
    case ArticleType.RENTAL:
      const { guests: rentalGuests, ...rentalRest } = criteria as CustomRentalAccommodationFilterInput;
      return {
        rentalAccommodation: {
          ...rentalRest,
          ...!!rentalGuests && { guests: Object.values(rentalGuests).reduce((acc, val) => acc + val, 0) }
        } as RentalAccommodationArticleFilterInput
      };
    case ArticleType.SALE:
      return { saleAccommodation: criteria as SaleAccommodationArticleFilterInput };
  }
}

export const formatSort = (type: ArticleType | "sold", sort: AnyArticleSortInput[]) => {
  switch (type) {
    case ArticleType.RENTAL:
      return { rentalAccommodation: sort };
    case ArticleType.SALE:
    case "sold":
      return { saleAccommodation: sort };
  }
}

export const useGQLSearchFormatter = (pageSize: number, type: ArticleType | "sold", searchParams: URLSearchParams) => {

  const isMounted = useIsMounted();
  const [ ctx ] = useSearchContext();
  const [ criteria, setCriteria ] = useState<AnyArticleSearch>(criteriaTools.parse(searchParams, type));
  const [ pagination, setPagination ] = useState<PaginationPayload>(paginationTools.parse(searchParams));

  useEffect(() => {
    if (!isMounted) return;
    setCriteria(criteriaTools.parse(searchParams, type));
    setPagination(paginationTools.parse(searchParams));
  }, [ type, searchParams ]);

  const where = useMemo(() => formatWhere({ ...(ctx[type].forced || {}), ...criteria }), [ criteria ]);

  const others = useMemo(() => ({
    ...(pagination.direction === "after") && {
      first: pageSize,
      ...(!!pagination.cursor) && { after: pagination.cursor },
    },
    ...(pagination.direction === "before") && {
      last: pageSize,
      ...(!!pagination.cursor) && { before: pagination.cursor },
    }
  }), [ pagination ]);

  return { where, ...others };

}
